import React, { useEffect, useState } from 'react';
import { useAuth } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Switch } from './ui/switch';
import { CheckCircle2, Mail, Package, BellOff } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Communication preferences widget — embedded in the Terms page (and reusable anywhere).
 * - Anonymous: shows opt-in form (subscribe to newsletter).
 * - Logged-in: shows a toggle that opts out / opts back in by hitting the existing
 *   /api/newsletter/unsubscribe + /api/newsletter/resubscribe endpoints. Token-based
 *   unsubscribe is also honored (footer links in emails).
 */
const CommunicationPreferences = () => {
  const { user, token } = useAuth();
  const [prefs, setPrefs] = useState({ newsletter_unsubscribed: false, inventory_notifications_unsubscribed: false, all_communications_unsubscribed: false });
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [optInSent, setOptInSent] = useState(false);

  // For logged-in users, fetch current granular preference state.
  useEffect(() => {
    if (!user || !token) return;
    let cancelled = false;
    const load = async () => {
      try {
        const res = await fetch(`${API_URL}/api/me/communication-preferences`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) {
          setPrefs({
            newsletter_unsubscribed: !!data.newsletter_unsubscribed,
            inventory_notifications_unsubscribed: !!data.inventory_notifications_unsubscribed,
            all_communications_unsubscribed: !!data.all_communications_unsubscribed,
          });
        }
      } catch (_) { /* silent */ }
    };
    load();
    return () => { cancelled = true; };
  }, [user, token]);

  // `which` = 'news' | 'inv' | 'all'. `subscribed` = the toggle's ON state (subscribed).
  const handleToggle = async (which, subscribed) => {
    if (!user || !token) return;
    const optOut = !subscribed;
    let next = { ...prefs };
    if (which === 'all') {
      next = {
        all_communications_unsubscribed: optOut,
        newsletter_unsubscribed: optOut,
        inventory_notifications_unsubscribed: optOut,
      };
    } else if (which === 'news') {
      next.newsletter_unsubscribed = optOut;
      if (!optOut) next.all_communications_unsubscribed = false;
    } else if (which === 'inv') {
      next.inventory_notifications_unsubscribed = optOut;
      if (!optOut) next.all_communications_unsubscribed = false;
    }
    setPrefs(next);
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/me/communication-preferences`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(next),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || 'Could not update preference');
      setPrefs({
        newsletter_unsubscribed: !!data.newsletter_unsubscribed,
        inventory_notifications_unsubscribed: !!data.inventory_notifications_unsubscribed,
        all_communications_unsubscribed: !!data.all_communications_unsubscribed,
      });
      toast.success('Email preferences updated.');
    } catch (e) {
      toast.error(e.message);
      // reload true state on failure
      try {
        const r = await fetch(`${API_URL}/api/me/communication-preferences`, { headers: { Authorization: `Bearer ${token}` } });
        if (r.ok) { const d = await r.json(); setPrefs({ newsletter_unsubscribed: !!d.newsletter_unsubscribed, inventory_notifications_unsubscribed: !!d.inventory_notifications_unsubscribed, all_communications_unsubscribed: !!d.all_communications_unsubscribed }); }
      } catch (_) {}
    } finally {
      setLoading(false);
    }
  };

  const handleAnonOptIn = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/newsletter/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), source: 'terms_page' }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || 'Could not subscribe right now.');
      setOptInSent(true);
      setEmail('');
      toast.success(data.new_subscriber ? 'Welcome aboard!' : 'You\'re already on the list.');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const PrefRow = ({ icon: Icon, title, desc, subscribed, disabled, testid, danger, onChange }) => (
    <div className={`flex items-center justify-between gap-4 p-4 rounded-lg border ${danger ? 'border-red-200 bg-red-50/40' : 'border-slate-200 bg-slate-50'}`}>
      <div className="flex items-start gap-3 min-w-0">
        <Icon className={`h-5 w-5 mt-0.5 flex-shrink-0 ${danger ? 'text-red-500' : 'text-teal-700'}`} />
        <div className="min-w-0">
          <p className="font-medium text-slate-900">{title}</p>
          <p className="text-sm text-slate-500">{desc}</p>
          <p className={`text-xs mt-0.5 font-medium ${subscribed ? 'text-teal-600' : 'text-red-600'}`}>{subscribed ? 'Subscribed' : 'Opted out'}</p>
        </div>
      </div>
      <Switch checked={subscribed} disabled={disabled} onCheckedChange={onChange} data-testid={testid} />
    </div>
  );

  if (user && token) {
    const allOut = prefs.all_communications_unsubscribed;
    return (
      <div className="space-y-3" data-testid="comm-prefs-toggle">
        <div>
          <p className="font-medium text-slate-900">Email preferences</p>
          <p className="text-sm text-slate-500">{user.email} — choose what you'd like to hear from us.</p>
        </div>
        <PrefRow
          icon={Package} title="Inventory & restock alerts" desc="Back-in-stock and new arrival emails."
          subscribed={!prefs.inventory_notifications_unsubscribed && !allOut} disabled={loading || allOut}
          testid="comm-prefs-inventory" onChange={(v) => handleToggle('inv', v)}
        />
        <PrefRow
          icon={Mail} title="Newsletter & research updates" desc="Articles, guides and member discounts."
          subscribed={!prefs.newsletter_unsubscribed && !allOut} disabled={loading || allOut}
          testid="comm-prefs-newsletter" onChange={(v) => handleToggle('news', v)}
        />
        <PrefRow
          icon={BellOff} title="All Genora communication" desc="Turn OFF to opt out of every marketing email."
          subscribed={!allOut} disabled={loading} danger
          testid="comm-prefs-all" onChange={(v) => handleToggle('all', v)}
        />
        <p className="text-xs text-slate-400">Transactional emails about your orders are always sent.</p>
      </div>
    );
  }

  if (optInSent) {
    return (
      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 inline-flex items-center gap-2" data-testid="comm-prefs-thanks">
        <CheckCircle2 className="h-5 w-5 text-emerald-600" />
        <span className="text-sm text-emerald-900 font-medium">Thanks — your preference has been recorded.</span>
      </div>
    );
  }

  return (
    <form onSubmit={handleAnonOptIn} className="bg-slate-50 border border-slate-200 rounded-lg p-4" data-testid="comm-prefs-anon-form">
      <p className="text-sm text-slate-700 mb-3">
        Want our research updates and member discounts? Add your email below — we send no more than 2 emails a month and you can unsubscribe in one click.
      </p>
      <div className="flex flex-col sm:flex-row gap-2">
        <label htmlFor="comm-prefs-email" className="sr-only">Email</label>
        <Input
          id="comm-prefs-email"
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@lab.com"
          className="flex-1"
          data-testid="comm-prefs-anon-email"
        />
        <Button
          type="submit"
          disabled={loading}
          className="bg-teal-700 hover:bg-teal-800 text-white"
          data-testid="comm-prefs-anon-submit"
        >
          {loading ? 'Subscribing…' : 'Subscribe'}
        </Button>
      </div>
    </form>
  );
};

export default CommunicationPreferences;
